import i18next from "i18next";
import type { BackendModule, ReadCallback, ResourceKey } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enTranslation from "../../locales/en/translation.json";
import {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
  isRTLLanguage,
  isSupportedLanguage,
  mapNavigatorLanguage,
  type LanguageCode,
} from "./languages";

export {
  DEFAULT_LANGUAGE,
  FALLBACK_LANGUAGE,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
  isRTLLanguage,
  isSupportedLanguage,
  mapNavigatorLanguage,
};
export type { LanguageCode };

/**
 * Vite turns this glob into an object keyed by file path with lazy-loading
 * functions. Each value resolves to the imported JSON module on first call.
 */
const TRANSLATION_LOADERS = import.meta.glob<{
  default: Record<string, ResourceKey>;
}>([
  "../../locales/*/translation.json",
  "!../../locales/en/translation.json",
]);

/**
 * Custom i18next backend that loads each language's JSON via the Vite glob
 * loaders above. Avoids any HTTP backend or external bundler config — works
 * identically in dev and prod.
 */
const viteGlobBackend: BackendModule = {
  type: "backend",
  init: () => {
    /* no-op */
  },
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    const key = `../../locales/${language}/translation.json`;
    const loader = TRANSLATION_LOADERS[key];
    if (!loader) {
      callback(new Error(`Missing translation file for "${language}".`), false);
      return;
    }
    loader()
      .then((mod) => callback(null, mod.default))
      .catch((err: unknown) => {
        callback(
          err instanceof Error ? err : new Error(String(err)),
          false,
        );
      });
  },
};

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

i18next
  .use(viteGlobBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // English is bundled eagerly so the first paint never waits on a fetch.
    resources: {
      en: { translation: enTranslation },
    },
    partialBundledLanguages: true,

    fallbackLng: FALLBACK_LANGUAGE,
    defaultNS: "translation",
    ns: ["translation"],
    supportedLngs: SUPPORTED_CODES,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",

    interpolation: {
      escapeValue: false, // React already escapes
    },

    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: STORAGE_KEY,
      convertDetectedLanguage: (lng) => mapNavigatorLanguage(lng),
    },

    returnObjects: true,
    react: {
      useSuspense: false,
    },
  });

export function changeLanguage(code: LanguageCode): Promise<unknown> {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // localStorage may be unavailable
  }
  return i18next.changeLanguage(code);
}

export default i18next;
